/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Component, createMemo, createSignal, For, JSX, Show } from 'solid-js';
import Icon from '../Icon/Icon';
import CustomTooltip, {
  TooltipPositions,
} from '../CustomTooltip/CustomTooltip';

export interface DropDownOption {
  text?: string;
  /** Stable identity for selection. When set, `selected` matches this instead of `text`. */
  value?: string;
  icon?: string;
  testId?: string;
  hasSubMenu?: boolean;
  disabledReasonTooltip?: string;
}

export interface DropdownOptionsProps {
  /** The alignment for dropdown content */
  position?:
    | 'left'
    | 'right'
    | 'top left'
    | 'top right'
    | 'middle'
    | 'top middle';
  /** The position of the icon incase options type is "both" and indicate on which side icon should be displayed */
  /** Default is left */
  iconPosition?: 'left' | 'right';
  /** The classname for content. */
  className?: string;
  /** The type of Dropdown options */
  /** Default is text */
  optionsType: 'text' | 'icon' | 'both';
  /** The dropdown options */
  dropDownOptions: string[] | DropDownOption[];
  /** The default selected item */
  selected?: string | string[];
  /** The style to be overridden */
  style?: string | JSX.CSSProperties;
  /** Custom CSS class for dropdown positioning */
  customPositionClass?: string;
  /** Indicates whether options to be shown or hidden */
  isOpen: boolean;
  /** unsetHeight - Removes scroll inside the dropdown */
  setWidth?: string;
  unsetHeight?: boolean;
  addSeparators?: number[];
  addGroupTitles?: { [key: string]: string };
  disabledItem?: number[];
  fabButton?: boolean;
  customMenuItemIcon?: string;
  tooltipTitle?: string;
  tooltipPosition?: string;
  tooltipDisplayArrow?: boolean;
  tooltipClassName?: string;
  // Callback props for event handling
  onKeepOpen?(keepOpen: boolean): void;
  onSelectionChanged?(selected: string, index: number): void;
}

const DropdownOptions: Component<DropdownOptionsProps> = (props) => {
  const [showSubcomp, setShowSubcomp] = createSignal(false);

  const getClassNames = (...classes: (string | undefined | boolean)[]) => {
    return classes.filter(Boolean).join(' ');
  };

  const dropDownOptions = createMemo(() => {
    const options: DropDownOption[] = [];
    const optionsType = props.optionsType || 'text';

    if (optionsType === 'text') {
      for (const option of props.dropDownOptions as string[]) {
        options.push({ text: option });
      }
    } else if (optionsType === 'both') {
      return props.dropDownOptions as DropDownOption[];
    } else if (optionsType === 'icon') {
      for (const option of props.dropDownOptions as string[]) {
        options.push({ icon: option });
      }
    }
    return options;
  });

  const openSubcomponentMenu = (showSubcomp: boolean) => {
    setShowSubcomp(showSubcomp);

    // Call callback if provided
    if (props.onKeepOpen) {
      props.onKeepOpen(showSubcomp);
    }
  };

  const handleSelection = (item: DropDownOption | string, index: number) => {
    const selectedValue = typeof item === 'string' ? item : item.text || '';

    // Call callback if provided
    if (props.onSelectionChanged) {
      props.onSelectionChanged(selectedValue, index);
    }
  };

  const renderMenuItem = (item: DropDownOption) => {
    if (!item.icon) {
      if (isSelected(item)) {
        return (
          <>
            <span class="block text">{item.text}</span>
            <Show
              when={props.customMenuItemIcon}
              fallback={<i class="icon-font tc-icon-check" />}
            >
              <i class={getClassNames(props.customMenuItemIcon, 'icon-font')} />
            </Show>
          </>
        );
      } else {
        return <>{item.text}</>;
      }
    }

    switch (props.iconPosition) {
      case 'right': {
        return (
          <>
            <span class="block text">{item.text}</span>
            <Icon
              size={'icon-small'}
              icon={item.icon}
              class={getClassNames(
                item.icon,
                'text-gray_8 icon-right icon-font'
              )}
            />
          </>
        );
      }
      case 'left':
      default: {
        return (
          <>
            <Icon
              size={'icon-small'}
              icon={item.icon}
              class={getClassNames(
                item.icon,
                'text-gray_8 icon-left icon-font pr-1'
              )}
            />
            <span class="block text">{item.text}</span>
          </>
        );
      }
    }
  };

  const isDisabled = (index: number) => {
    return props.disabledItem && props.disabledItem.includes(index);
  };

  const hasGroupTitle = (index: number) => {
    return (
      props.addGroupTitles &&
      // eslint-disable-next-line no-prototype-builtins
      props.addGroupTitles.hasOwnProperty(`index_${index.toString()}`)
    );
  };

  const getGroupTitle = (index: number) => {
    return props.addGroupTitles?.[`index_${index.toString()}`] || '';
  };

  const hasSeparator = (index: number) => {
    return props.addSeparators && props.addSeparators.includes(index);
  };

  function getOptionIdentity(item: DropDownOption): string | undefined {
    return item.value ?? item.text;
  }

  function isSelected(item: DropDownOption) {
    const identity = getOptionIdentity(item);
    if (identity === undefined) {
      return false;
    }
    if (Array.isArray(props.selected)) {
      return props.selected.includes(identity);
    }
    if (typeof props.selected === 'string') {
      return identity === props.selected;
    }
    return false;
  }

  return (
    <Show when={props.dropDownOptions.length > 0}>
      <div
        class={getClassNames(
          'dropdown-pane',
          props.fabButton && 'fab-button',
          props.setWidth || 'w-auto',
          props.isOpen && 'is-open',
          props.position,
          props.unsetHeight && 'remove-height',
          props.className,
          props.customPositionClass
        )}
        style={props.style}
      >
        <ul class="dropdown-list">
          <For each={dropDownOptions()}>
            {(item, index) => (
              <>
                <Show when={hasGroupTitle(index())}>
                  <li
                    class="group-title text-meta"
                    textContent={getGroupTitle(index())}
                  />
                </Show>

                <Show when={isDisabled(index()) && !showSubcomp()}>
                  <li
                    class={getClassNames(
                      'list-item',
                      'disabled not-allowed',
                      isSelected(item) && 'active'
                    )}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    <CustomTooltip
                      text={item.disabledReasonTooltip ?? props.tooltipTitle}
                      position={
                        (props.tooltipPosition as
                          | TooltipPositions
                          | undefined) ?? 'bottom'
                      }
                      arrow={props.tooltipDisplayArrow}
                      tooltipClass={props.tooltipClassName}
                      disableToolTip={
                        !item.disabledReasonTooltip && !props.tooltipTitle
                      }
                      class="w-full"
                    >
                      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
                      {renderMenuItem(item)}
                    </CustomTooltip>
                  </li>
                </Show>

                <Show when={!isDisabled(index()) || showSubcomp()}>
                  <Show when={showSubcomp()}>
                    <Show when={index() === 0}>
                      <li
                        class="list-item"
                        onClick={() => {
                          openSubcomponentMenu(false);
                        }}
                        textContent="Back"
                      />
                    </Show>
                  </Show>

                  <Show when={!showSubcomp()}>
                    <li
                      class={getClassNames(
                        'list-item',
                        isSelected(item) && 'active'
                      )}
                      data-testid={item.testId ?? item.text}
                      data-cy={item.text}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.hasSubMenu) {
                          openSubcomponentMenu(true);
                        } else {
                          handleSelection(item, index());
                        }
                      }}
                    >
                      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
                      {renderMenuItem(item)}
                      <Show when={item.hasSubMenu}>
                        <i class="icon-font tc-icon-chevron-right i18 icon-right" />
                      </Show>
                    </li>
                  </Show>
                </Show>

                <Show when={hasSeparator(index())}>
                  <hr />
                </Show>
              </>
            )}
          </For>
        </ul>
      </div>
    </Show>
  );
};

export default DropdownOptions;
